import get from 'lodash/get';
import * as constants from './constants';
import { createReducer } from '../helpers';

export const initialState = {
  canViewAllFlockTabs: false,
  canUseAdminApp: false,
  canMockAll: false,
  canMockAssigned: false,
  canMockPartner: false,
  canUseRogbot: false,
  canViewFlock: false,
  canViewPartner: false,
  canViewIntegrations: false,
  canViewTestInvite: false,
  canViewSkyhookButton: false,
  canViewLocationFeatures: false,
  canViewOrganizationFeatures: false,
  canDeleteSmsRequest: false,
  canViewPartnerModifications: false,
  canGraduateLocation: false,
  canAssignUserToOrg: false,
  canViewProfileDetails: false,
  fetchingPermissions: true,
  userRole: '',
  isCsmAdmin: '',
  canUsePodium: '',
  canUseApp: '',
  canUseTools: '',
  canReadLimitedLocations: '',
  canReadLimitedOrganizations: '',
  canUpdateLocations: '',
  canUpdateOrganizations: '',
  canUpdateLocationToggles: '',
  canUpdateOrgUsers: '',
  canEditFeedback: '',
  canEditLocationAutomations: '',
  canEditLocationIframe: '',
  canEditLocationInvitation: '',
  canEditLocLimits: '',
  canEditLocReviewSites: '',
  canEditLocToggles: '',
  canEditOrgUsers: '',
  canEditWebchat: '',
  canEnableReporting: '',
  canIndexOrgs: '',
  canArchiveWidget: '',
};

export const getCanViewFlock = state => state.canViewFlock;
export const getCanViewPartner = state => state.canViewPartner;
export const getCanAccessRogbot = state => state.canUseRogbot;
export const getCanViewAllFlockTabs = state => state.canViewAllFlockTabs;
export const getCanGraduateLocation = state => state.canGraduateLocation;
export const getCanAssignUserToOrg = state => state.canAssignUserToOrg;
export const getCanViewProfileDetails = state => state.canViewProfileDetails;
export const checkUserRole = state => state.userRole;
export const getCanUseTools = state => state.canUseTools;
export const getCanViewIntegrations = state => state.canViewIntegrations;

function prettyPermissions(permissions) {
  return permissions.reduce((prev, curr) => {
    prev[curr.slug] = curr.authorized;
    return prev;
  }, {});
}

function setSkyhookPermissions(state, { permissions }) {
  const prettyPerms = prettyPermissions(permissions);
  return {
    ...state,
    canUseAdminApp: get(prettyPerms, 'skyhook_can_use_app', false),
    canViewAllFlockTabs: get(
      prettyPerms,
      'skyhook_can_view_all_flock_tabs',
      false
    ),
    canMockAll: get(prettyPerms, 'skyhook_can_mock_all', false),
    canMockAssigned: get(prettyPerms, 'skyhook_can_mock_assigned', false),
    canMockPartner: get(prettyPerms, 'skyhook_can_mock_partner', false),
    canViewFlock: get(prettyPerms, 'skyhook_can_view_flock', false),
    canViewPartner: get(prettyPerms, 'skyhook_can_view_partner', false),
    canUseRogbot: get(prettyPerms, 'skyhook_can_use_rogbot', false),
    canGraduateLocation: get(
      prettyPerms,
      'skyhook_can_graduate_location',
      false
    ),
    canAssignUserToOrg: get(
      prettyPerms,
      'skyhook_can_assign_user_to_ocs',
      false
    ),
    fetchingPermissions: false,
  };
}

function setOrganizationPermissions(state, { permissions }) {
  const prettyPerms = prettyPermissions(permissions);
  return {
    ...state,
    canUseAdminApp: get(prettyPerms, 'skyhook_can_use_app', false),
    canMockAll: get(prettyPerms, 'skyhook_can_mock_all', false),
    canMockAssigned: get(prettyPerms, 'skyhook_can_mock_assigned', false),
    canMockPartner: get(prettyPerms, 'skyhook_can_mock_partner', false),
    canViewFlock: get(prettyPerms, 'skyhook_can_view_flock', false),
    canViewPartner: get(prettyPerms, 'skyhook_can_view_partner', false),
    canViewTestInvite: get(prettyPerms, 'skyhook_can_send_test_invite', false),
    canViewSkyhookButton: get(
      prettyPerms,
      'skyhook_can_view_skyhook_button',
      false
    ),
    canViewLocationFeatures: get(
      prettyPerms,
      'skyhook_can_view_location_features',
      false
    ),
    canViewOrganizationFeatures: get(
      prettyPerms,
      'skyhook_can_view_organization_features',
      false
    ),
    canDeleteSmsRequest: get(
      prettyPerms,
      'skyhook_can_delete_sms_request',
      false
    ),
    canArchiveWidget: get(
      prettyPerms,
      'webchat_can_archive_widget_in_any_org',
      false
    ),
    fetchingPermissions: false,
  };
}

function setDefaultPermissions(state, { permissions }) {
  const prettyPerms = prettyPermissions(permissions);
  return {
    ...state,
    canUseAdminApp: get(prettyPerms, 'skyhook_can_use_app', false),
    canViewFlock: get(prettyPerms, 'skyhook_can_view_flock', false),
    canViewPartner: get(prettyPerms, 'skyhook_can_view_partner', false),
    canViewProfileDetails: get(
      prettyPerms,
      'skyhook_can_view_profile_details',
      false
    ),
    fetchingPermissions: false,
  };
}

function fetchingPermissions(state) {
  return {
    ...state,
    fetchingPermissions: true,
  };
}

function stopFetchingPermissions(state) {
  return {
    ...state,
    fetchingPermissions: false,
  };
}

function setAdminPermissions(state, { permissions }) {
  let permissionsMap = permissions.flatMap(({ permission }) => {
    return { [permission]: permission };
  });
  permissionsMap = Object.assign(...permissionsMap);
  return {
    ...state,
    ...permissionsMap,
    fetchingPermissions: false,
  };
}

function setRole(state, { permissions }) {
  return {
    ...state,
    userRole: get(permissions[0], 'role', ''),
    fetchingPermissions: false,
  };
}

export default createReducer(initialState, {
  [constants.PERMISSIONS_REQUEST]: fetchingPermissions,
  [constants.SKYHOOK_PERMISSIONS_SUCCESS]: setSkyhookPermissions,
  [constants.PARTNER_PERMISSIONS_SUCCESS]: stopFetchingPermissions,
  [constants.ORGANIZATION_PERMISSIONS_SUCCESS]: setOrganizationPermissions,
  [constants.DEFAULT_PERMISSIONS_SUCCESS]: setDefaultPermissions,
  [constants.ROLE_PERMISSIONS_SUCCESS]: setRole,
  [constants.ROLE_PERMISSIONS_FAILURE]: stopFetchingPermissions,
  [constants.SUPPORT_PERMISSIONS_SUCCESS]: setAdminPermissions,
  [constants.SUPPORT_PERMISSIONS_FAILURE]: stopFetchingPermissions,
  [constants.SKYHOOK_PERMISSIONS_FAILURE]: stopFetchingPermissions,
  [constants.PARTNER_PERMISSIONS_FAILURE]: stopFetchingPermissions,
  [constants.ORGANIZATION_PERMISSIONS_FAILURE]: stopFetchingPermissions,
  [constants.DEFAULT_PERMISSIONS_FAILURE]: stopFetchingPermissions,
});
